import React, { useState, useRef, useEffect } from "react";
import supabase from "../../../src/supabaseClient";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { registerUser } from "../../store/auth-slice";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";

const VPO_DEPARTMENT = {
  name: "Vice President of Operations",
  type: "internal",
  prefix: "vpo",
  short_prefix: "vpo",
};

const CreateUser = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [credentials, setCredentials] = useState({
    firstName: "",
    lastName: "",
    userName: "",
    password: "",
    department: "",
    role: "",
    created_by_id: user.id,
    created_by_username: user.user_name,
  });

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentPrefixes, setDepartmentPrefixes] = useState({});
  const roles = ["Superadmin", "Employee", "Corporate"];
  const API_URL = process.env.REACT_APP_API_URL;
  const departmentRef = useRef(null);
  const passwordRef = useRef(null);
  const roleRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);

  // Fetch departments from API on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/departments?status=active`);
        const depts = response.data.departments || [];

        // Set all departments for state
        const hasVpo = depts.some(
          (dept) => String(dept.name || "").toLowerCase() === VPO_DEPARTMENT.name.toLowerCase()
        );
        setDepartments(hasVpo ? depts : [...depts, VPO_DEPARTMENT]);

        // Build prefix mapping from fetched departments
        const prefixMap = {};
        (hasVpo ? depts : [...depts, VPO_DEPARTMENT]).forEach(dept => {
          const normalizedName = dept.name.toLowerCase().replace(/\s+/g, '');
          prefixMap[normalizedName] = {
            default: dept.prefix || normalizedName,
            short: dept.short_prefix || dept.name.substring(0, 2).toLowerCase(),
            type: dept.type
          };
        });
        setDepartmentPrefixes(prefixMap);
      } catch (error) {
        console.error('Error fetching departments:', error);
        toast.error('Failed to load departments');
      }
    };

    fetchDepartments();
  }, [API_URL]);

  // Filter departments based on selected role
  const getFilteredDepartments = () => {
    if (credentials.role === 'Corporate') {
      return departments.filter(d => d.type === 'corporate');
    }
    return departments.filter(d => d.type === 'internal');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedCredentials = { ...credentials, [name]: value };

    // If role changes to Corporate, clear first name and last name
    if (name === 'role' && value === 'Corporate') {
      updatedCredentials = {
        ...updatedCredentials,
        firstName: '',
        lastName: '',
        department: '' // Also clear department to force re-selection from corporate list
      };
    }

    // If role changes from Corporate to something else, clear department
    if (name === 'role' && value !== 'Corporate' && credentials.role === 'Corporate') {
      updatedCredentials = {
        ...updatedCredentials,
        department: ''
      };
    }

    setCredentials(updatedCredentials);

    if (["department", "firstName", "lastName", "role"].includes(name)) {
      generateUsername(updatedCredentials);
    }
  };

  const generateUsername = async (updatedCredentials) => {
    try {
      const { department, firstName, lastName, role } = updatedCredentials;
      const normalizeDepartment = (dept) =>
        dept.toLowerCase().replace(/\s+/g, "");

      if (!department) return;

      const normalizedDepartment = normalizeDepartment(department);
      const normalizedRole = (role || "").toLowerCase();

      // For corporate role: use department prefix (e.g., "terumo", "samsung")
      // For other roles: use short prefix if has name, default prefix if no name
      let departmentPrefix;
      let searchPrefix; // Prefix to search for existing users

      if (normalizedRole === "corporate") {
        departmentPrefix = departmentPrefixes[normalizedDepartment]?.default || normalizedDepartment;
        searchPrefix = "cr"; // Search for all "cr" users to get next number
      } else {
        departmentPrefix = !firstName && !lastName
          ? departmentPrefixes[normalizedDepartment]?.default || "brokerage"
          : departmentPrefixes[normalizedDepartment]?.short || "br";
        searchPrefix = departmentPrefix;
      }

      const { data: users, error } = await supabase
        .from("users")
        .select("user_name")
        .ilike("user_name", `${searchPrefix}%`);

      if (error) throw error;

      const departmentUsers = users || [];
      const lastNumber =
        departmentUsers
          .map((user) =>
            parseInt(
              user.user_name.replace(searchPrefix, "").split("-")[0],
              10
            )
          )
          .filter((num) => !isNaN(num))
          .sort((a, b) => b - a)[0] || 0;

      const formattedName =
        firstName && lastName
          ? `${
              firstName.trim().charAt(0).toUpperCase() +
              firstName.trim().slice(1).toLowerCase()
            }${
              lastName.trim().charAt(0).toUpperCase() +
              lastName.trim().slice(1).toLowerCase()
            }`
          : "";

      let newUserName;
      if (normalizedRole === "corporate") {
        // For corporate users: cr00-[departmentPrefix] (e.g., cr01-terumo, cr02-samsung)
        newUserName = `cr${String(lastNumber + 1).padStart(2, "0")}-${departmentPrefix}`;
      } else if (!firstName && !lastName) {
        newUserName = `${departmentPrefix}${String(lastNumber + 1).padStart(
          2,
          "0"
        )}`;
      } else {
        newUserName = `${departmentPrefix}${String(lastNumber + 1).padStart(
          2,
          "0"
        )}-${formattedName}`;
      }

      setCredentials((prevCredentials) => ({
        ...prevCredentials,
        userName: newUserName,
      }));
    } catch (err) {
      console.error("Error generating username:", err);
      toast.error("Error generating username. Please try again.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(registerUser(credentials)).then((data) => {
      if (data?.payload?.success) {
        toast.success("User Created Successfully", { position: "top-right" });
      } else {
        toast.error(data?.payload?.message, { position: "top-right" });
      }
    });
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <section
      id="create-user"
      className="flex flex-col justify-center p-0 font-poppins min-h-screen bg-gray-100 relative overflow-auto"
    >
     
      <div className="relative flex flex-col justify-center py-6 px-4">
        {/* Enhanced Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-3">
          
            <h1 className="text-4xl font-bold  text-blue">
              Create User
            </h1>
          </div>
          <p className="text-grey font-medium">
            Create a new user account for your organization
          </p>
        </div>

        {/* Modern Form Container */}
        <div className="w-full max-w-7xl mx-auto">
          <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden">
            {/* Form Header */}
            <div className="bg-blue px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <i className="fas fa-user-cog text-white text-xl"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-xl">User Information</h2>
                  <p className="text-white text-sm">Fill in the details below to create a new account</p>
                </div>
              </div>
            </div>

            {/* Form Body */}
            <div className="p-8">
              <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Personal Information Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center">
                      <i className="fas fa-id-card text-blue text-sm"></i>
                    </div>
                    <h3 className="text-blue font-bold text-lg">Personal Info</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-user text-blue text-xs"></i>
                        First Name <span className="text-grey/50 text-xs font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={credentials.firstName}
                        ref={firstNameRef}
                        onChange={handleInputChange}
                        disabled={credentials.role === 'Corporate'}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-grey/50"
                        placeholder={credentials.role === 'Corporate' ? 'Not required for corporate users' : 'Enter first name'}
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-user text-blue text-xs"></i>
                        Last Name <span className="text-grey/50 text-xs font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        ref={lastNameRef}
                        value={credentials.lastName}
                        onChange={handleInputChange}
                        disabled={credentials.role === 'Corporate'}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-grey/50"
                        placeholder={credentials.role === 'Corporate' ? 'Not required for corporate users' : 'Enter last name'}
                      />
                    </div>
                  </div>
                </div>

                {/* Role & Department Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center">
                      <i className="fas fa-briefcase text-blue text-sm"></i>
                    </div>
                    <h3 className="text-blue font-bold text-lg">Organization</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-building text-blue text-xs"></i>
                        Department <span className="text-red">*</span>
                      </label>
                      <div className="relative">
                        <select
                          name="department"
                          value={credentials.department}
                          onChange={handleInputChange}
                          ref={departmentRef}
                          className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                        >
                          <option value="" className="text-gray-500 font-semibold">
                            {credentials.role === 'Corporate' ? 'Select Corporate Client' : 'Select Department'}
                          </option>
                          {getFilteredDepartments().map((dept, index) => (
                            <option
                              key={index}
                              value={dept.name}
                              className="text-gray-500 font-semibold"
                            >
                              {dept.name === "Vice President of Operations" ? "Vice President of Operations (VPO)" : dept.name}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                          <i className="fas fa-chevron-down text-blue text-sm"></i>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-shield-alt text-blue text-xs"></i>
                        Role <span className="text-red">*</span>
                      </label>
                      <div className="relative">
                        <select
                          name="role"
                          value={credentials.role}
                          onChange={(e) =>
                            setCredentials({ ...credentials, role: e.target.value })
                          }
                          ref={roleRef}
                          className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                        >
                          <option value="" className="text-gray-500 font-semibold">
                            Select Role
                          </option>
                          {roles.map((role, index) => (
                            <option
                              key={index}
                              value={role}
                              className="text-gray-500 font-semibold"
                            >
                              {role}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                          <i className="fas fa-chevron-down text-blue text-sm"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Credentials Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center">
                      <i className="fas fa-key text-blue text-sm"></i>
                    </div>
                    <h3 className="text-blue font-bold text-lg">Credentials</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-at text-blue text-xs"></i>
                        Username <span className="text-grey/50 text-xs font-normal">(Auto-generated)</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="userName"
                          value={credentials.userName}
                          disabled
                          className="w-full p-4 border-2 border-gray-200 rounded-2xl bg-gray-50 text-grey cursor-not-allowed"
                          placeholder="Auto Generated"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                          <i className="fas fa-magic text-grey/50 text-sm"></i>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-lock text-blue text-xs"></i>
                        Password <span className="text-red">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={passwordVisible ? "text" : "password"}
                          name="password"
                          value={credentials.password}
                          onChange={(e) =>
                            setCredentials({ ...credentials, password: e.target.value })
                          }
                          ref={passwordRef}
                          placeholder="Enter secure password"
                          className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md pr-12"
                          required
                          title="Password must be at least 8 characters long, include at least one uppercase letter and one number."
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-grey hover:text-blue transition-colors duration-200 p-1"
                        >
                          {passwordVisible ? <FaEye size={16} /> : <FaEyeSlash size={16} />}
                        </button>
                      </div>
                      <p className="text-xs text-grey/70 mt-2 flex items-center gap-1">
                        <i className="fas fa-info-circle text-blue text-xs"></i>
                        Must contain 8+ characters with uppercase, lowercase & numbers
                      </p>
                    </div>
                  </div>
                </div>

                {/* Enhanced Submit Button */}
                <div className="lg:col-span-3 mt-6 pt-4 border-t border-gray-100">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-4 px-8 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] ${
                      loading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue hover:from-blue/90 hover:to-blue text-yellow shadow-xl hover:shadow-2xl"
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center gap-3">
                        <i className="fas fa-spinner fa-spin"></i>
                        Creating Account...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-3">
                        <i className="fas fa-user-plus"></i>
                        CREATE ACCOUNT
                      </div>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreateUser;
